import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { useSearchParams } from 'react-router';
import type { AutopilotMark, ContactHit, ConversationSummary, Message } from '@zenda/api-contract';
import { ClientTag } from '../components/ClientTag';
import { TagEditor } from '../components/TagEditor';
import { EmptyState } from '../components/EmptyState';
import { Icon } from '../components/Icon';
import { Popover } from '../components/Popover';
import { LinhaDeConversa } from '../components/LinhaDeConversa';
import { MessageBubble } from '../components/MessageBubble';
import { useCorrecao, usePrecisaDeVoce } from '../lib/piloto';
import {
  FaixaDoPiloto,
  PilotoNoCabecalho,
  PilotoNoCompositor,
} from '../components/PilotoNaConversa';
import {
  AutorizacaoNaConversa,
  NovaConversaModal,
  useIrParaConversa,
} from '../components/IniciarConversa';
import { FichaDoContato } from '../components/FichaDoContato';
import { RespostasProntas } from '../components/RespostasProntas';
import { ReabrirComTemplate } from '../components/ReabrirComTemplate';
import { Skeleton, SkeletonRows } from '../components/Skeleton';
import { sendMedia, sendTyping } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useEscrevendo } from '../lib/presenca';
import { textoDoErro } from '../lib/aviso-de-erro';
import { precisaDeAtencao } from '../lib/urgencia';
import {
  useAssignConversation,
  useClients,
  useContactSearch,
  useConversation,
  useConversations,
  useReadConversation,
  useReopenConversation,
  useFocus,
  useNomesDaEquipe,
  useResolveConversation,
  useSaveFocus,
  useSendMessage,
} from '../lib/queries';
import { formatCountdown, formatDayLong, formatPhone, initials } from '../lib/format';
import type { Dictionary } from '../content/pt-BR';
import { useTip } from '../components/Tip';
import { useT } from '../lib/i18n';
import '../styles/inbox.css';

const FORMATOS_ACEITOS =
  '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.jpg,.jpeg,.png,.mp3,.ogg,.opus,.m4a,.aac,.amr,.mp4,.3gp';

type Filter = 'all' | 'needs_you' | 'awaiting' | 'resolved' | 'mine' | 'unassigned';

function filters(t: Dictionary): readonly { readonly id: Filter; readonly label: string }[] {
  return [
    { id: 'all', label: t.inbox.filters.all },
    { id: 'needs_you', label: t.piloto.estados.needs_human },
    { id: 'awaiting', label: t.inbox.filters.awaiting },
    { id: 'resolved', label: t.inbox.filters.resolved },
    { id: 'mine', label: t.inbox.filters.mine },
    { id: 'unassigned', label: t.inbox.filters.unassigned },
  ];
}

/**
 * A ficha do contato comeca RECOLHIDA: a largura ganha vai para a conversa, que e onde
 * a atendente passa o dia. A preferencia fica no navegador porque e do jeito de trabalhar
 * dela, nao do dado — e nao vale uma ida ao servidor.
 */
const SIDE_KEY = 'zenda.inbox.ficha';

function readSidePreference(): boolean {
  return window.localStorage.getItem(SIDE_KEY) === 'aberta';
}

function matches(c: ConversationSummary, filter: Filter, eu: string | null): boolean {
  switch (filter) {
    case 'needs_you':
      return (
        c.autopilot.state === 'needs_human' ||
        (c.autopilot.state === 'taken_over' && c.awaitingReply)
      );
    case 'mine':
      return c.assigneeId !== null && c.assigneeId === eu;
    case 'unassigned':
      return c.assigneeId === null && c.status !== 'resolved';
    case 'awaiting':
      return c.awaitingReply;
    case 'resolved':
      return c.status === 'resolved';
    case 'all':
      return true;
  }
}

type ItemDoFio =
  | { readonly tipo: 'msg'; readonly at: string; readonly m: Message }
  | { readonly tipo: 'marca'; readonly at: string; readonly marca: AutopilotMark };

interface DayGroup {
  readonly day: string;
  readonly label: string;
  readonly items: ItemDoFio[];
}

function dayLabel(t: Dictionary, iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date(today.getTime() - 86_400_000);
  if (d.toDateString() === today.toDateString()) return t.inbox.today;
  if (d.toDateString() === yesterday.toDateString()) return t.inbox.yesterday;
  return formatDayLong(iso);
}

/** As mensagens e as faixas do piloto numa linha do tempo só, por dia. */
function groupByDay(
  t: Dictionary,
  messages: readonly Message[],
  marcas: readonly AutopilotMark[],
): DayGroup[] {
  const itens: ItemDoFio[] = [
    ...messages.map((m) => ({ tipo: 'msg' as const, at: m.occurredAt, m })),
    ...marcas.map((marca) => ({ tipo: 'marca' as const, at: marca.at, marca })),
  ].sort((a, b) => a.at.localeCompare(b.at));
  const groups: DayGroup[] = [];
  for (const item of itens) {
    const day = new Date(item.at).toDateString();
    const last = groups[groups.length - 1];
    if (last !== undefined && last.day === day) {
      last.items.push(item);
    } else {
      groups.push({ day, label: dayLabel(t, item.at), items: [item] });
    }
  }
  return groups;
}

export function InboxPage(): ReactElement {
  const t = useT();
  const tip = useTip();
  const [params, setParams] = useSearchParams();
  const [filter, setFilter] = useState<Filter>(() =>
    new URLSearchParams(window.location.search).get('filtro') === 'precisa' ? 'needs_you' : 'all',
  );
  const [soUrgentes, setSoUrgentes] = useState(false);
  const [term, setTerm] = useState('');
  const [draft, setDraft] = useState('');
  /** 'resposta' = texto livre · 'template' = reabrir depois das 24 h · 'nota' = só interna. */
  const [modo, setModo] = useState<'resposta' | 'template' | 'nota'>('resposta');
  const isNote = modo === 'nota';
  const [subindo, setSubindo] = useState(false);
  const [erroEnvio, setErroEnvio] = useState<string | null>(null);
  const arquivoRef = useRef<HTMLInputElement | null>(null);
  const campoRef = useRef<HTMLTextAreaElement | null>(null);
  const [sideOpen, setSideOpen] = useState<boolean>(readSidePreference);
  /** Qual consulta esta esperando confirmacao de desmarque, se alguma. */

  const { me } = useAuth();
  const eu = me?.userId ?? null;
  const clients = useClients();
  const foco = useFocus();
  const salvarFoco = useSaveFocus();

  /**
   * O filtro por cliente vive na URL (`?cliente=<id>`), nao no estado local: e assim que
   * a pagina do cliente consegue mandar a atendente direto para as conversas dele — e
   * que ela consegue mandar esse link para si mesma amanha.
   */
  /**
   * Agora e uma LISTA: `?cliente=id1,id2`. Vazio significa todos.
   *
   * Continua na URL, e nao no estado local, pelo mesmo motivo de antes — e assim
   * que a pagina do cliente manda a atendente direto para as conversas dele, e
   * assim que ela manda o link para si mesma amanha. A lista so tornou o link
   * mais expressivo: "as tres clinicas de segunda" cabe num endereco.
   */
  const clientIds = (params.get('cliente') ?? '').split(',').filter(Boolean);
  // O link de um cliente abre a conversa dele mesmo fora do foco: o foco decide o que
  // avisa, e não o que se pode abrir.
  const conversations = useConversations(clientIds.length > 0);

  // O contador da barra lateral manda para cá com `?filtro=precisa`.
  const filtroDaUrl = params.get('filtro');
  useEffect(() => {
    if (filtroDaUrl === 'precisa') setFilter('needs_you');
  }, [filtroDaUrl]);

  const precisaDeVoce = usePrecisaDeVoce(filter === 'needs_you');
  const fonte = filter === 'needs_you' ? precisaDeVoce.data : conversations.data;

  const visible = useMemo(() => {
    const needle = term.trim().toLowerCase();
    const digitos = needle.replace(/\D+/g, '');
    const lista = (fonte ?? []).filter(
      (c) =>
        matches(c, filter, eu) &&
        (!soUrgentes || precisaDeAtencao(c)) &&
        (clientIds.length === 0 || clientIds.includes(c.clientId)) &&
        (needle === '' ||
          c.contactName.toLowerCase().includes(needle) ||
          c.clientName.toLowerCase().includes(needle) ||
          (digitos.length >= 4 && (c.phone ?? c.waId).replace(/\D+/g, '').includes(digitos))),
    );
    // Em "Precisa de você", quem espera há mais tempo primeiro (plano §6.2).
    return filter === 'needs_you'
      ? lista.sort((a, b) =>
          (a.autopilot.changedAt ?? '').localeCompare(b.autopilot.changedAt ?? ''),
        )
      : lista;
  }, [fonte, filter, soUrgentes, params, term, eu]);

  const contatos = useContactSearch(term);
  const contatosSemConversa = useMemo(() => {
    const naLista = new Set(visible.map((c) => c.contactId));
    return (contatos.data ?? []).filter((h) => !naLista.has(h.contactId));
  }, [contatos.data, visible]);
  const [escrevendoPara, setEscrevendoPara] = useState<ContactHit | null>(null);
  const irParaConversa = useIrParaConversa();

  const urgentes = useMemo(
    () =>
      (conversations.data ?? []).filter((c) => precisaDeAtencao(c) && c.status !== 'resolved')
        .length,
    [conversations.data],
  );

  const selectedId = params.get('c') ?? visible[0]?.id ?? null;
  /** Mensagem a destacar ao abrir — e assim que o player volta ao audio dele. */
  const alvo = params.get('m');
  const detail = useConversation(selectedId);
  const send = useSendMessage(selectedId ?? '');
  const resolve = useResolveConversation(selectedId ?? '');
  const reopen = useReopenConversation(selectedId ?? '');
  const marcarLida = useReadConversation();
  const atribuir = useAssignConversation(selectedId ?? '');
  const nomesDaEquipe = useNomesDaEquipe();
  const escrevendo = useEscrevendo(selectedId, eu).flatMap((id) => {
    const nome = nomesDaEquipe.get(id);
    return nome === undefined ? [] : [nome];
  });
  const ultimoAvisoDeEscrita = useRef(0);

  const threadRef = useRef<HTMLDivElement | null>(null);
  /**
   * Se a atendente está no fim da conversa AGORA.
   *
   * Guardado, e não medido na hora de decidir: quando o anexo carrega, o
   * conteúdo já cresceu, e medir naquele instante diz "ela saiu do fim" —
   * exatamente quando ela não saiu de lugar nenhum. O valor é atualizado pela
   * rolagem dela, que é a única coisa que expressa intenção.
   */
  const grudado = useRef(true);
  /** Qual conversa o scroll já levou ao fim — para saber o que é troca de conversa. */
  const ultimaConversa = useRef<string | null>(null);
  const messages = detail.data?.messages ?? [];

  /**
   * A conversa abre no FIM, e continua no fim enquanto ela estiver lá.
   *
   * ── Por que não basta rolar quando chega mensagem ──
   * A prévia de anexo carrega DEPOIS: o `fetch` autenticado devolve o blob
   * segundos após o balão existir, e a imagem cresce o conteúdo em algumas
   * centenas de pixels. O scroll feito na hora do render fica curto, e a última
   * mensagem — justamente a que ela abriu para ler — termina fora da vista.
   *
   * O `ResizeObserver` resolve na raiz: qualquer coisa que mude a altura
   * dispara de novo, sem a tela precisar saber o que cresceu.
   *
   * ── Por que só quando ela JÁ está no fim ──
   * Se estiver lendo o histórico e chegar mensagem nova, puxar a tela para
   * baixo tira a linha que ela estava lendo. A margem de 120 px é a folga de
   * "está no fim, só não exatamente no último pixel".
   */
  useEffect(() => {
    const node = threadRef.current;
    if (node === null) return;

    const irAoFim = (): void => {
      node.scrollTop = node.scrollHeight;
    };

    /**
     * Trocar de conversa e receber mensagem NÃO são a mesma coisa.
     *
     * Abrir uma conversa vai para o fim sempre — é onde está o que ela quer
     * ler. Mensagem nova só vai para o fim se ela já estivesse lá; se estava
     * lendo o histórico, puxar a tela tira a linha que ela estava lendo.
     *
     * Sem esta distinção o efeito rodava igual nos dois casos e a segunda
     * situação ficava quebrada — reproduzido: rolando no meio da conversa, uma
     * mensagem nova saltava a tela para o fim.
     */
    if (ultimaConversa.current !== selectedId) {
      ultimaConversa.current = selectedId;
      grudado.current = true;
    }
    if (grudado.current && alvo === null) irAoFim();

    const observer = new ResizeObserver(() => {
      if (grudado.current && alvo === null) irAoFim();
    });
    // Observa o CONTEUDO, e nao o container: o container tem altura fixa, e
    // observa-lo nunca dispararia.
    for (const filho of Array.from(node.children)) observer.observe(filho);

    return () => observer.disconnect();
  }, [messages.length, selectedId, alvo]);

  /**
   * Voltar ao audio que esta tocando.
   *
   * O alvo sai da URL depois de honrado: sem isso, qualquer re-render tardio
   * arrastaria a tela de volta para uma mensagem que ela ja deixou para tras.
   */
  useEffect(() => {
    if (alvo === null) return;
    const el = threadRef.current?.querySelector(`[data-msg="${alvo}"]`);
    if (!(el instanceof HTMLElement)) return;

    grudado.current = false;
    el.scrollIntoView({ block: 'center' });
    el.classList.add('is-alvo');

    const relogio = setTimeout(() => {
      el.classList.remove('is-alvo');
      setParams(
        (atuais) => {
          const proximos = new URLSearchParams(atuais);
          proximos.delete('m');
          return proximos;
        },
        { replace: true },
      );
    }, 2200);
    return () => clearTimeout(relogio);
  }, [alvo, messages.length, setParams]);

  /**
   * O campo acompanha o texto.
   *
   * `height = 'auto'` antes de ler o `scrollHeight` nao e supersticao: sem
   * zerar, o valor lido e o da altura ATUAL, e o campo so cresce — nunca volta
   * a encolher quando a pessoa apaga.
   */
  useEffect(() => {
    const el = campoRef.current;
    if (el === null) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [draft, isNote, selectedId]);

  const conversation = detail.data?.conversation;

  /** A mensagem do piloto que ela está corrigindo, se alguma (plano §6.3). */
  const [corrigindo, setCorrigindo] = useState<string | null>(null);
  const corrigir = useCorrecao(selectedId ?? '');

  /**
   * A sugestão do copiloto entra no campo quando ele está vazio, uma vez por sugestão: Enter
   * envia como se fosse dela (plano §6.3). Se ela já escreveu algo, a sugestão não apaga.
   */
  const sugestao = detail.data?.autopilotDetail.draft ?? null;
  const sugestaoAplicada = useRef<string | null>(null);
  useEffect(() => {
    if (sugestao === null || sugestaoAplicada.current === sugestao.id) return;
    sugestaoAplicada.current = sugestao.id;
    setDraft((atual) => (atual.trim() === '' ? sugestao.body : atual));
  }, [sugestao?.id]);
  const usandoSugestao = sugestao !== null && draft === sugestao.body;

  /**
   * Abrir a conversa marca como lida — e reagir a `unreadCount` cobre o caso de
   * a mensagem chegar com a conversa JA aberta: o polling traz o numero, e sem
   * isto o badge apareceria numa conversa que a atendente esta olhando.
   *
   * `marcarLida.mutate` fica fora das dependencias de proposito: o objeto de
   * mutacao muda de identidade a cada render, e incluí-lo faria o efeito rodar
   * em loop.
   */
  const naoLidas = conversation?.unreadCount ?? 0;
  /**
   * Conversa na tela é conversa lida.
   *
   * Tentei antes exigir clique explícito, com medo de que só chegar no inbox
   * limpasse o badge da primeira conversa. Estava errado: a lista AUTO-SELECIONA
   * a primeira, então as mensagens dela ficam abertas na tela e o badge
   * continuava marcando como não lida a conversa que a pessoa está lendo. Badge
   * que contradiz o que está à vista é pior que badge que some cedo demais.
   */
  // Conversa de médico de outra operação registra a abertura mesmo já lida: a dona vê
  // quem de fora abriu cada conversa (equipes.md §15.1, D5). O servidor agrupa em 15 min.
  const deFora = conversation?.mine === false;
  useEffect(() => {
    if (selectedId === null || (naoLidas === 0 && !deFora)) return;
    marcarLida.mutate(selectedId);
  }, [selectedId, naoLidas, deFora]);
  /**
   * O texto quebrado em pedaços, separando o que sobrou de marcador.
   *
   * A mensagem pronta entra no campo já com o que deu para preencher. O que não
   * tinha valor continua como `{retorno}`, e é isso que a camada de realce pinta
   * de vermelho — o buraco fica visível no lugar exato onde está, e o envio fica
   * travado até ela apagar e escrever à mão.
   *
   * O bloqueio vive só aqui, e não no servidor: para o servidor a mensagem é
   * texto, e `{alguma coisa}` pode ser legítimo. Quem sabe que aquilo era um
   * marcador é esta tela.
   */
  const pedacos = useMemo(() => {
    const saida: { texto: string; marcador: boolean }[] = [];
    let ultimo = 0;
    for (const m of draft.matchAll(/\{[a-z_]+\}/g)) {
      const ini = m.index;
      if (ini > ultimo) saida.push({ texto: draft.slice(ultimo, ini), marcador: false });
      saida.push({ texto: m[0], marcador: true });
      ultimo = ini + m[0].length;
    }
    if (ultimo < draft.length) saida.push({ texto: draft.slice(ultimo), marcador: false });
    return saida;
  }, [draft]);

  const faltaPreencher = pedacos.some((p) => p.marcador);
  const realceRef = useRef<HTMLDivElement | null>(null);

  /**
   * A agenda do paciente e a proxima consulta do cabecalho vem da MESMA fonte.
   * Antes o cabecalho lia a tabela de mockup e o painel nao existia — a tela
   * dizia "sem consulta" logo abaixo do que acabara de ser marcado.
   */

  const windowOpen = conversation?.serviceWindow.open ?? false;

  /**
   * Fechada a janela, o padrão é o TEMPLATE, e não a nota interna.
   *
   * A nota não chega à paciente. Abrir nela quando a atendente precisa avisar
   * alguém empurra para o caminho que não resolve — e era o único que existia.
   */
  useEffect(() => {
    if (conversation === undefined) return;
    setModo(conversation.serviceWindow.open ? 'resposta' : 'template');
  }, [conversation?.id, conversation?.serviceWindow.open]);

  function toggleSide(): void {
    setSideOpen((open) => {
      window.localStorage.setItem(SIDE_KEY, open ? 'recolhida' : 'aberta');
      return !open;
    });
  }

  function select(id: string): void {
    const next = new URLSearchParams(params);
    next.set('c', id);
    setParams(next);
    setDraft('');
    setCorrigindo(null);
    setErroEnvio(null);
  }

  // Trocar de cliente com uma conversa aberta de OUTRO cliente deixaria a thread da
  // direita contradizendo a lista da esquerda. A selecao cai junto com o filtro.
  function aplicarClientes(ids: readonly string[]): void {
    const next = new URLSearchParams(params);
    if (ids.length === 0) next.delete('cliente');
    else next.set('cliente', ids.join(','));
    next.delete('c');
    setParams(next);
    setDraft('');
  }

  const focados = foco.data?.doctorIds ?? null;
  const medicosVisiveis = (clients.data ?? []).flatMap((c) =>
    c.doctorId === null ? [] : [c.doctorId],
  );
  const noFoco = (doctorId: string | null): boolean =>
    doctorId !== null && (focados === null || focados.includes(doctorId));

  // Marcar todos volta a `null`, e não à lista: assim o médico que alguém compartilhar
  // amanhã entra no foco sem a pessoa precisar lembrar de marcá-lo.
  function alternarFoco(doctorId: string): void {
    const proximo =
      focados === null
        ? [doctorId]
        : focados.includes(doctorId)
          ? focados.filter((x) => x !== doctorId)
          : [...focados, doctorId];
    if (proximo.length === 0) return;
    const todos = medicosVisiveis.every((d) => proximo.includes(d));
    salvarFoco.mutate(todos ? null : proximo);
  }

  /** Marca ou desmarca de uma vez os médicos de uma dona. */
  function alternarGrupo(doctorIds: readonly string[]): void {
    const todosNoFoco = focados !== null && doctorIds.every((d) => focados.includes(d));
    const base = focados ?? [];
    const proximo = todosNoFoco
      ? base.filter((d) => !doctorIds.includes(d))
      : [...new Set([...base, ...doctorIds])];
    if (proximo.length === 0) return;
    const todos = medicosVisiveis.every((d) => proximo.includes(d));
    salvarFoco.mutate(todos ? null : proximo);
  }

  /** Avisa a equipe que alguém está escrevendo, no máximo a cada 3 s. */
  function avisarEscrita(): void {
    if (selectedId === null || isNote) return;
    const agora = Date.now();
    if (agora - ultimoAvisoDeEscrita.current < 3_000) return;
    ultimoAvisoDeEscrita.current = agora;
    void sendTyping(selectedId).catch(() => undefined);
  }

  /**
   * Toda guarda de envio mora AQUI, e nao no `disabled` do botao.
   *
   * O `disabled` cobre so o clique. O Enter chama esta funcao direto, e enquanto
   * a checagem de marcador vivia apenas no botao dava para mandar
   * "seu retorno e em {retorno}" para o paciente apertando Enter — o botao
   * cinza ao lado dizendo que nao dava.
   */
  function submit(): void {
    const body = draft.trim();
    if (body === '' || selectedId === null) return;
    if (!windowOpen && !isNote) return;
    if (faltaPreencher) return;
    setErroEnvio(null);
    const correcao = corrigindo !== null && !isNote ? corrigindo : null;
    send.mutate(isNote ? { body, isInternalNote: true } : { body }, {
      onSuccess: () => {
        if (correcao !== null) corrigir.mutate({ messageId: correcao, texto: body });
        setCorrigindo(null);
      },
      // O texto volta para o campo: apagar o que ela escreveu num envio recusado
      // obriga a redigitar, e em silencio parece que foi.
      onError: (err) => {
        setDraft((atual) => (atual === '' ? body : atual));
        setErroEnvio(textoDoErro(err));
      },
    });
    setDraft('');
  }

  /**
   * O que estiver escrito no campo vira LEGENDA do anexo.
   *
   * É o comportamento do WhatsApp, e é o que a pessoa espera: ela escreve
   * "segue o pedido de exame", anexa o PDF e manda uma coisa só. Mandar duas
   * mensagens separadas seria surpresa.
   */
  async function enviarArquivo(file: File): Promise<void> {
    if (selectedId === null || !windowOpen) return;
    // A legenda sai do MESMO campo, entao carrega o mesmo marcador por preencher.
    if (faltaPreencher) return;
    setErroEnvio(null);
    setSubindo(true);
    try {
      await sendMedia(selectedId, file, draft);
      setDraft('');
      await detail.refetch();
      await conversations.refetch();
    } catch (err) {
      setErroEnvio(err instanceof Error ? err.message : t.ui.falhaNoArquivo);
    } finally {
      setSubindo(false);
    }
  }

  const clientOptions = [...(clients.data ?? [])].sort((a, b) =>
    a.name.localeCompare(b.name, 'pt-BR'),
  );

  const rotuloEstado = filters(t).find((f) => f.id === filter)?.label ?? t.inbox.filters.all;

  /**
   * Um cliente escolhido mostra o NOME; varios mostram a contagem.
   *
   * "2 clientes" e menos informativo que "Clinica Bem-Estar", mas o nome de dois
   * clientes nao cabe no botao e cortar no meio ("Clinica Bem-Es...") e pior que
   * contar. Quais sao exatamente esta a um clique, no proprio popover.
   *
   * Sem selecao o botao diz so "Todos" — "Todos os clientes" nao cabia e saia
   * truncado. O item DENTRO do painel continua com o texto inteiro, onde ha
   * largura e onde a palavra sozinha seria ambigua ao lado dos nomes.
   */
  const focadosNaTela = clientOptions.filter((c) => c.doctorId !== null && noFoco(c.doctorId));
  const rotuloClientes =
    focados === null
      ? t.inbox.allClientsShort
      : focadosNaTela.length === 1
        ? (focadosNaTela[0]?.name ?? t.inbox.allClientsShort)
        : t.inbox.clientsChosen(focadosNaTela.length);
  const daEquipe = clientOptions.filter((c) => c.access !== 'casa');
  const grupos = [
    { titulo: t.inbox.foco.daCasa, itens: clientOptions.filter((c) => c.access === 'casa') },
    ...[...new Set(daEquipe.map((c) => c.ownerName ?? ''))].map((dona) => ({
      titulo: t.inbox.foco.daDona(dona),
      itens: daEquipe.filter((c) => (c.ownerName ?? '') === dona),
    })),
  ].filter((g) => g.itens.length > 0);
  const foraDoAlcance =
    focados === null ? 0 : focados.filter((d) => !medicosVisiveis.includes(d)).length;
  const soEste =
    clientIds.length === 0
      ? null
      : clientIds.length === 1
        ? (clientOptions.find((c) => c.id === clientIds[0])?.name ?? t.inbox.clientsChosen(1))
        : t.inbox.clientsChosen(clientIds.length);

  // Sem conversa escolhida a ficha nao tem o que mostrar: reservar a coluna deixaria um
  // vazio de 296 px no lugar onde a conversa deveria estar.
  const showSide = sideOpen && selectedId !== null && !detail.isError;

  return (
    <div className={`inbox${showSide ? ' is-side-open' : ''}`}>
      {/* ── Coluna 1: filtros + lista unificada ───────────────────────────── */}
      <section className="card card--flush inbox__list" aria-label={t.pages.inbox.title}>
        <div className="inbox__filters">
          <div className="inbox__search">
            <Icon name="search" size={15} />
            <input
              value={term}
              maxLength={80}
              onChange={(e) => setTerm(e.target.value)}
              placeholder={t.inbox.searchPlaceholder}
              aria-label={t.inbox.searchPlaceholder}
            />
          </div>

          {/* Tres controles numa linha so: estado, clientes, urgencia.
              Antes eram cinco chips (que nao cabiam em portugues) mais um
              `<select>` numa linha propria. O `<select>` nativo tambem nao
              servia mais: filtrar por DOIS clientes e pedido normal de quem
              atende varios, e select simples nao faz isso. */}
          <div className="chips">
            <Popover rotulo={t.common.search} resumo={rotuloEstado} ativo={filter !== 'all'}>
              {(fechar) => (
                <>
                  {filters(t).map((f, i) => (
                    <div key={f.id}>
                      {/* Divisor depois de "Todas": ela e a AUSENCIA de filtro,
                          as outras sao recortes. Sao coisas de natureza
                          diferente na mesma lista. */}
                      {i === 1 || i === 4 ? <div className="pop__divider" /> : null}
                      <button
                        type="button"
                        className={`pop__item${filter === f.id ? ' is-on' : ''}`}
                        onClick={() => {
                          setFilter(f.id);
                          fechar();
                        }}
                      >
                        {/* Circulo, e nao quadrado: estes tres se EXCLUEM.
                            Usar caixa de marcar aqui prometeria poder marcar
                            dois, e a primeira tentativa desmentiria a promessa. */}
                        <span
                          className={`pop__radio${filter === f.id ? ' is-on' : ''}`}
                          aria-hidden="true"
                        />
                        <span className="pop__label">{f.label}</span>
                      </button>
                    </div>
                  ))}
                </>
              )}
            </Popover>

            <Popover
              rotulo={t.inbox.filterByClient}
              resumo={rotuloClientes}
              ativo={focados !== null}
            >
              {() => (
                <>
                  <button
                    type="button"
                    role="checkbox"
                    aria-checked={focados === null}
                    className={`pop__item${focados === null ? ' is-on' : ''}`}
                    onClick={() => salvarFoco.mutate(null)}
                  >
                    <span
                      className={`pop__check${focados === null ? ' is-on' : ''}`}
                      aria-hidden="true"
                    >
                      {focados === null ? <Icon name="check" size={11} /> : null}
                    </span>
                    <span className="pop__label">{t.inbox.allClients}</span>
                  </button>
                  <p className="pop__hint">{t.inbox.foco.explica}</p>
                  {foraDoAlcance === 0 ? null : (
                    <p className="pop__hint pop__hint--aviso">
                      {t.inbox.foco.foraDoAlcance(foraDoAlcance)}
                    </p>
                  )}

                  {grupos.map((grupo) => (
                    <div key={grupo.titulo}>
                      <div className="pop__divider" />
                      {daEquipe.length === 0 ? null : (
                        <button
                          type="button"
                          className="pop__group"
                          onClick={() =>
                            alternarGrupo(
                              grupo.itens.flatMap((c) => (c.doctorId === null ? [] : [c.doctorId])),
                            )
                          }
                        >
                          {grupo.titulo}
                        </button>
                      )}
                      {grupo.itens.map((c) => {
                        const on = focados !== null && noFoco(c.doctorId);
                        return (
                          <button
                            key={c.id}
                            type="button"
                            role="checkbox"
                            aria-checked={on}
                            className={`pop__item${on ? ' is-on' : ''}`}
                            onClick={() => {
                              if (c.doctorId !== null) alternarFoco(c.doctorId);
                            }}
                            disabled={c.doctorId === null}
                          >
                            <span className={`pop__check${on ? ' is-on' : ''}`} aria-hidden="true">
                              {on ? <Icon name="check" size={11} /> : null}
                            </span>
                            <span className="dot" style={{ background: c.color }} />
                            <span className="pop__label">{c.name}</span>
                          </button>
                        );
                      })}
                    </div>
                  ))}
                </>
              )}
            </Popover>

            {soEste === null ? null : (
              <button
                type="button"
                className="chip is-active"
                onClick={() => aplicarClientes([])}
                {...tip(t.inbox.foco.tirarSoEste)}
              >
                {t.inbox.foco.soEste(soEste)}
                <Icon name="x" size={11} />
              </button>
            )}

            {/* Eixo proprio, e por isso separado por uma barra: os dois da
                esquerda recortam a lista, este atravessa qualquer recorte. A
                contagem no rotulo evita o clique que descobre uma lista
                vazia. */}
            <span className="chips__sep" aria-hidden="true" />
            <button
              type="button"
              aria-pressed={soUrgentes}
              className={`chip chip--urg${soUrgentes ? ' is-active' : ''}`}
              onClick={() => setSoUrgentes((v) => !v)}
            >
              {t.inbox.filters.urgent}
              {urgentes === 0 ? '' : ` ${urgentes}`}
            </button>
          </div>
        </div>

        <div className="inbox__items scroll-y">
          {conversations.isPending ? (
            <div style={{ padding: 'var(--space-4)' }}>
              <SkeletonRows rows={6} height={72} />
            </div>
          ) : visible.length === 0 && contatosSemConversa.length === 0 ? (
            <EmptyState icon="search" title={t.inbox.emptyTitle} text={t.inbox.emptyText} />
          ) : (
            visible.map((c) => (
              <LinhaDeConversa
                key={c.id}
                conversa={c}
                ativa={c.id === selectedId}
                onSelecionar={() => select(c.id)}
              />
            ))
          )}
          {contatosSemConversa.length === 0 ? null : (
            <div className="contatos">
              <p className="contatos__titulo">{t.novaConversa.contatos}</p>
              {contatosSemConversa.map((h) => (
                <button
                  key={h.contactId}
                  type="button"
                  className="contato"
                  disabled={irParaConversa.indo}
                  onClick={() => {
                    const unico = h.clients.length === 1 ? h.clients[0] : undefined;
                    if (unico === undefined) setEscrevendoPara(h);
                    else irParaConversa.ir(unico.clientId, h.contactId);
                  }}
                >
                  <span className="conv__av">{initials(h.name)}</span>
                  <span className="contato__id">
                    <span className="contato__nome">{h.name}</span>
                    <span className="contato__meta">
                      {h.phone === null ? '' : `${formatPhone(h.phone)} · `}
                      {t.novaConversa.contatoDe(h.clients.map((c) => c.clientName).join(', '))}
                    </span>
                  </span>
                  <Icon name="chat" size={15} />
                </button>
              ))}
            </div>
          )}
        </div>
        {escrevendoPara === null ? null : (
          <NovaConversaModal contato={escrevendoPara} onClose={() => setEscrevendoPara(null)} />
        )}
      </section>

      {/* ── Coluna 2: thread ──────────────────────────────────────────────── */}
      <section className="card card--flush inbox__thread" aria-label={t.inbox.conversation}>
        {selectedId === null ? (
          <EmptyState icon="chat" title={t.inbox.pickTitle} text={t.inbox.pickText} />
        ) : detail.isError ? (
          <EmptyState icon="chat" title={t.inbox.sumiu} text={t.inbox.sumiuTexto} />
        ) : detail.isPending || conversation === undefined ? (
          <div style={{ padding: 'var(--space-6)', display: 'grid', gap: 16 }}>
            <Skeleton h={44} radius="var(--radius-md)" />
            <SkeletonRows rows={5} height={54} />
          </div>
        ) : (
          <>
            <header className="thread__head">
              <div className="thread__id">
                {/* O telefone vive AQUI e nao no card da lista: na lista ele
                    competiria com o nome em vinte e oito linhas, e aqui e o dado
                    que a atendente copia para ligar ou conferir se e a pessoa
                    certa. O avatar de iniciais saiu — repetia a inicial de um
                    nome escrito ao lado, em corpo maior. */}
                <p className="thread__name">
                  {conversation.contactName}
                  {conversation.phone === null ? null : (
                    <span className="thread__phone num">
                      {formatPhone(conversation.phone ?? conversation.waId)}
                    </span>
                  )}
                </p>
                {/* Cliente primeiro, medico responsavel depois: e em nome dele que a
                    resposta sai. */}
                <p className="thread__sub">
                  <ClientTag
                    name={conversation.clientName}
                    color={conversation.clientColor}
                    size="md"
                  />
                  {/* Quando o cliente e um medico sozinho, o nome do cliente E o
                      nome do medico — e a linha aparecia duas vezes, lado a lado,
                      parecendo defeito. So mostra o medico quando ele acrescenta
                      informacao: dentro de uma clinica com varios. */}
                  {conversation.ownerName === null ? null : (
                    <span className="thread__dona">{t.inbox.da(conversation.ownerName)}</span>
                  )}
                  {conversation.doctorName === conversation.clientName ? null : (
                    <span className="thread__doctor">
                      <span className="dot" style={{ background: conversation.doctorColor }} />
                      {conversation.doctorName}
                    </span>
                  )}
                </p>
                {/* As tags vivem aqui, e nao num menu escondido: marcar precisa custar
                    um clique, senao ninguem marca e a organizacao nao acontece. */}
                <TagEditor
                  conversationId={conversation.id}
                  tags={conversation.tags}
                  podeCriar={conversation.mine}
                />
              </div>
              {/* UM controle, nao dois — mas ele PRECISA voltar.
                  Antes, resolvida virava um selo estatico e nao havia como
                  desfazer: um clique errado so se corrigia quando o paciente
                  escrevesse de novo, ou seja, esperando outra pessoa consertar
                  o proprio engano. */}
              <PilotoNoCabecalho conversa={conversation} />
              {conversation.assigneeId !== null && conversation.assigneeId === eu ? (
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  onClick={() => atribuir.mutate({ userId: null })}
                  disabled={atribuir.isPending}
                >
                  <Icon name="user" size={15} />
                  {t.inbox.soltar}
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  onClick={() =>
                    atribuir.mutate({ userId: eu, takeOver: conversation.assigneeId !== null })
                  }
                  disabled={atribuir.isPending}
                  {...tip(t.inbox.assumirDica)}
                >
                  <Icon name="user" size={15} />
                  {conversation.assigneeName === null || conversation.assigneeId === null
                    ? t.inbox.assumir
                    : t.inbox.assumirDe(conversation.assigneeName)}
                </button>
              )}
              {conversation.status === 'resolved' ? (
                <button
                  type="button"
                  className="thread__done"
                  onClick={() => reopen.mutate()}
                  disabled={reopen.isPending}
                  {...tip(t.inbox.reopenHint)}
                >
                  <Icon name="check" size={14} />
                  {t.enums.conversationStatus.resolved}
                  <span className="thread__undo">{t.inbox.reopen}</span>
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  onClick={() => resolve.mutate()}
                  disabled={resolve.isPending}
                >
                  <Icon name="check" size={15} />
                  {t.inbox.resolve}
                </button>
              )}
              <button
                type="button"
                className={`btn btn--ghost btn--sm btn--icon${sideOpen ? ' is-on' : ''}`}
                onClick={toggleSide}
                aria-expanded={sideOpen}
                aria-controls="ficha-contato"
                {...tip(sideOpen ? t.inbox.collapseCard : t.inbox.expandCard)}
              >
                <Icon name="panel" size={16} />
                <span className="sr-only">{t.inbox.contactCard}</span>
              </button>
            </header>

            <div
              className="thread__body scroll-y"
              ref={threadRef}
              onScroll={(e) => {
                const el = e.currentTarget;
                // 120 px de folga: "no fim" não precisa ser o último pixel.
                grudado.current = el.scrollHeight - el.clientHeight - el.scrollTop < 120;
              }}
            >
              {messages.length === 0 ? (
                <EmptyState
                  icon="chat"
                  title={t.inbox.conversationEmpty}
                  text={t.inbox.conversationEmptyText}
                />
              ) : (
                groupByDay(t, messages, detail.data?.autopilotDetail.marks ?? []).map((group) => (
                  <div key={group.day} className="thread__group">
                    <div className="thread__day">
                      <span>{group.label}</span>
                    </div>
                    {group.items.map((item) =>
                      item.tipo === 'marca' ? (
                        <FaixaDoPiloto key={`marca-${item.at}`} marca={item.marca} />
                      ) : (
                        <MessageBubble
                          key={item.m.id}
                          message={item.m}
                          contato={conversation.contactName}
                          conversaId={conversation.id}
                          onCorrigir={(m) => {
                            setModo('resposta');
                            setDraft(m.body ?? '');
                            setCorrigindo(m.id);
                            campoRef.current?.focus();
                          }}
                        />
                      ),
                    )}
                  </div>
                ))
              )}
            </div>

            {/* A faixa vermelha "Janela fechada" saiu daqui.
                Ela ocupava uma tira inteira acima do campo para dizer o que o
                proprio campo agora diz: ele esta desabilitado, e o placeholder
                explica por que. Aviso que repete o estado do controle ao lado e
                ruido — e vermelho, num estado que acontece na maioria das
                conversas, deixa de significar alarme. */}
            <div className="composer">
              {detail.data === undefined ? null : (
                <PilotoNoCompositor detalhe={detail.data} usandoSugestao={usandoSugestao} />
              )}
              {corrigindo === null ? null : (
                <p className="piloto-sugestao">
                  <Icon name="robot" size={13} />
                  {t.piloto.corrigindo}
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    onClick={() => {
                      setCorrigindo(null);
                      setDraft('');
                    }}
                  >
                    {t.piloto.cancelar}
                  </button>
                </p>
              )}
              <div className="composer__tabs">
                <button
                  type="button"
                  className={`tab${modo === 'resposta' ? ' is-active' : ''}`}
                  onClick={() => setModo('resposta')}
                  disabled={!windowOpen}
                  {...(windowOpen ? {} : tip(t.inbox.window.closedReason))}
                >
                  {t.inbox.reply}
                </button>
                {/* Só aparece com a janela fechada: dentro dela o texto livre é
                    grátis, e um template ali seria cobrado à toa. */}
                {windowOpen ? null : (
                  <button
                    type="button"
                    className={`tab${modo === 'template' ? ' is-active' : ''}`}
                    onClick={() => setModo('template')}
                    {...tip(t.ui.templateReabre)}
                  >
                    Template
                  </button>
                )}
                <button
                  type="button"
                  className={`tab${modo === 'nota' ? ' is-active' : ''}`}
                  onClick={() => setModo('nota')}
                >
                  {t.inbox.internalNote}
                </button>
                {/* A contagem regressiva sobrevive, em texto pequeno: saber que
                    faltam duas horas muda a ordem em que ela responde. */}
                {windowOpen && conversation.serviceWindow.minutesRemaining !== null ? (
                  <span className="composer__window">
                    {t.inbox.windowClosingIn(
                      formatCountdown(conversation.serviceWindow.minutesRemaining),
                    )}
                  </span>
                ) : null}
              </div>

              {erroEnvio === null ? null : <p className="composer__erro">{erroEnvio}</p>}
              {escrevendo.length === 0 ? null : (
                <p className="composer__escrevendo" aria-live="polite">
                  {t.inbox.escrevendo(escrevendo)}
                </p>
              )}

              {/* Diz O QUE fazer, não só que está errado: o marcador em vermelho
                  no campo mostra onde, e esta linha diz o que colocar no lugar. */}
              {faltaPreencher ? (
                <p className="composer__falta-aviso">
                  <Icon name="alert" size={13} />
                  Este paciente não tem{' '}
                  {pedacos
                    .filter((p) => p.marcador)
                    .map((p) => p.texto)
                    .join(', ')}{' '}
                  na ficha. Apague o trecho em vermelho e escreva à mão para poder enviar.
                </p>
              ) : null}

              {modo === 'template' && detail.data?.precisaAutorizacao === true ? (
                <AutorizacaoNaConversa
                  clientId={conversation.clientId}
                  contactId={conversation.contactId}
                  conversationId={conversation.id}
                />
              ) : modo === 'template' ? (
                <ReabrirComTemplate
                  clientId={conversation.clientId}
                  conversationId={conversation.id}
                  onEnviado={() => setDraft('')}
                />
              ) : (
                <div className={`composer__box${isNote ? ' is-note' : ''}`}>
                  {/* Anexo só na aba de resposta: nota interna nunca sai daqui, e
                    um clipe ao lado dela prometeria algo que não acontece. */}
                  {isNote ? null : (
                    <>
                      <input
                        ref={arquivoRef}
                        type="file"
                        accept={FORMATOS_ACEITOS}
                        className="sr-only"
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          // O valor é limpo para o mesmo arquivo poder ser
                          // escolhido de novo depois de uma falha.
                          e.target.value = '';
                          if (f !== undefined) void enviarArquivo(f);
                        }}
                      />
                      <button
                        type="button"
                        className="composer__anexo"
                        onClick={() => arquivoRef.current?.click()}
                        disabled={!windowOpen || subindo || faltaPreencher}
                        aria-label={t.inbox.attach}
                        {...tip(windowOpen ? t.inbox.attach : t.inbox.window.closedReason)}
                      >
                        <Icon name={subindo ? 'clock' : 'paperclip'} size={17} />
                      </button>
                      <RespostasProntas
                        clientId={conversation.clientId}
                        variaveis={detail.data?.variaveis ?? {}}
                        onEscolher={(texto) => {
                          setDraft(texto);
                          campoRef.current?.focus();
                        }}
                      />
                    </>
                  )}
                  {/* Uma linha, crescendo com o texto.
                    Com `rows={2}` fixo, uma resposta curta ficava no topo de uma
                    caixa de duas linhas enquanto o clipe e o enviar ficavam na
                    base — os tres desalinhados entre si na maior parte do tempo,
                    que é quando a mensagem é curta. */}
                  {/* Camada espelhada ATRÁS do campo.
                    `textarea` não colore trecho nenhum do próprio valor. A única
                    forma de o marcador que sobrou aparecer em vermelho no lugar
                    onde ele está é desenhar o mesmo texto embaixo, com a mesma
                    tipografia, e deixar visível só o realce. */}
                  <div className="composer__campo">
                    <div className="composer__realce" aria-hidden="true" ref={realceRef}>
                      {pedacos.map((pedaco, i) =>
                        pedaco.marcador ? (
                          <mark key={i} className="composer__falta">
                            {pedaco.texto}
                          </mark>
                        ) : (
                          <span key={i}>{pedaco.texto}</span>
                        ),
                      )}
                    </div>
                    <textarea
                      ref={campoRef}
                      className="composer__input"
                      rows={1}
                      value={draft}
                      maxLength={4096}
                      disabled={!windowOpen && !isNote}
                      placeholder={
                        !windowOpen && !isNote
                          ? t.inbox.window.closedReason
                          : isNote
                            ? t.inbox.noteTooltip
                            : t.inbox.replyTo(conversation.contactName, conversation.clientName)
                      }
                      onChange={(e) => {
                        setDraft(e.target.value);
                        avisarEscrita();
                      }}
                      onScroll={(e) => {
                        // Sem isto, o realce fica parado enquanto o texto rola, e o
                        // vermelho passa a marcar a linha errada.
                        if (realceRef.current !== null) {
                          realceRef.current.scrollTop = e.currentTarget.scrollTop;
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          submit();
                        }
                      }}
                    />
                  </div>
                  {/* Só o ícone: o rótulo repetia o que a seta já diz, e ocupava
                    a largura que o campo de digitar precisa. O que ele faz
                    continua dito no `aria-label` e no tooltip — que é o mesmo
                    padrão do menu recolhido. */}
                  <button
                    type="button"
                    className="composer__enviar"
                    onClick={submit}
                    disabled={
                      draft.trim() === '' ||
                      send.isPending ||
                      faltaPreencher ||
                      (!windowOpen && !isNote)
                    }
                    aria-label={isNote ? t.inbox.saveNote : t.inbox.send}
                    {...tip(isNote ? t.inbox.saveNote : t.inbox.send)}
                  >
                    <Icon name={isNote ? 'note' : 'send'} size={16} />
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </section>

      {/* ── Coluna 3: ficha do contato, recolhida por padrao ───────────────── */}
      {!showSide ? null : (
        <FichaDoContato
          conversation={conversation}
          conversationId={selectedId}
          onTextoPronto={(texto) => {
            setDraft(texto);
            setModo('resposta');
            campoRef.current?.focus();
          }}
        />
      )}
    </div>
  );
}
